import * as PIXI from 'pixi.js';
import { TypedSerializableMixin } from '../../serializableMixin';
import { TypedUpdateFlowMixin } from '../../updateFlowMixin';
import {
    AlphaAttribute,
    ColorAttribute,
    NumberAttribute,
    getAttributesToSerialize
} from '../../attributes';
import { Config } from '../../../video-data/config';

export class BoxBorderGraphic extends TypedUpdateFlowMixin(TypedSerializableMixin(PIXI.Graphics)) {
    override _classType = 'BoxBorderGraphic';
    override _name = Config.BOX_BORDER_GRAPHIC;

    @NumberAttribute()
    public boxWidth = 150;
    @NumberAttribute()
    public boxHeight = 150;
    @NumberAttribute()
    public start0 = 0.25;
    @NumberAttribute()
    public end0 = 0.75;
    @NumberAttribute()
    public start1 = 0.75;
    @NumberAttribute()
    public end1 = 0.25;
    @NumberAttribute({ animatable: false })
    public horizontalPadding = 20;
    @NumberAttribute({ animatable: false })
    public verticalPadding = 20;
    @NumberAttribute({ min: 0, animatable: false })
    public borderThickness = 5;
    @ColorAttribute()
    public borderColor = 16777215;
    @AlphaAttribute()
    public borderAlpha = 0.5;

    _points = [];
    _segments = [];
    _length: number = null;

    positionOnSegment(uValue) {
        const position = uValue * this._length;
        let absUValue = 0;
        let nAbsUValue = 0;
        for (let i = 0; i < this._segments.length; i++) {
            nAbsUValue += this._segments[i];
            if (position >= absUValue && position <= nAbsUValue) {
                const ni = (i > this._points.length - 2) ? 0 : i + 1;
                const percentage = (position - absUValue) / this._segments[i];
                const x = (this._points[i][0] * (1 - percentage)) + (this._points[ni][0] * percentage);
                const y = (this._points[i][1] * (1 - percentage)) + (this._points[ni][1] * percentage);
                return {
                    index: i,
                    position: [x, y],
                };
            }
            absUValue += this._segments[i];
        }
    }

    lineForRange(start, end) {
        if (Math.abs(end - start) < 0.01) {
            return;
        }
        const spos = this.positionOnSegment(start % 1);
        const epos = this.positionOnSegment(end % 1);

        if (spos.index === epos.index) {
            this.moveTo(spos.position[0], spos.position[1]);
            this.lineTo(epos.position[0], epos.position[1]);
        }
        else if (spos.index < epos.index) {
            const nSegments = epos.index - spos.index;
            for (let i = 0; i < nSegments + 2; i++) {
                if (i === 0) {
                    this.moveTo(spos.position[0], spos.position[1]);
                }
                else if (i === nSegments + 1) {
                    this.lineTo(epos.position[0], epos.position[1]);
                }
                else {
                    this.lineTo(this._points[spos.index + i][0], this._points[spos.index + i][1]);
                }
            }
        }
        else {
            const nSegments = (this._segments.length - spos.index) + epos.index;
            for (let i = 0; i < nSegments + 2; i++) {
                if (i === 0) {
                    this.moveTo(spos.position[0], spos.position[1]);
                }
                else if (i === nSegments + 1) {
                    this.lineTo(epos.position[0], epos.position[1]);
                }
                else {
                    const index = (spos.index + i) % this._segments.length;
                    this.lineTo(this._points[index][0], this._points[index][1]);
                }
            }
        }
    }

    updateCoords() {
        const xMin = -this.horizontalPadding;
        const xMax = this.boxWidth + this.horizontalPadding;
        const yMin = -this.verticalPadding;
        const yMax = this.boxHeight + this.verticalPadding;

        this._points = [
            [xMin, yMin],
            [xMax, yMin],
            [xMax, yMax],
            [xMin, yMax],
        ];

        this._segments = this._points.map((point, index) => {
            const ci = index;
            let ni = index + 1;
            if (index === this._points.length - 1) {
                ni = 0;
            }
            const x = (this._points[ci][0] - this._points[ni][0]) ** 2;
            const y = (this._points[ci][1] - this._points[ni][1]) ** 2;
            return Math.sqrt(x + y);
        });

        this._length = this._segments.reduce((total, num) => {
            return total + num;
        });
    }

    override _update() {
        this.clear();
        this.updateCoords();
        this.lineStyle(this.borderThickness, this.borderColor, this.borderAlpha);
        this.lineForRange(this.start0, this.end0);
        this.lineForRange(this.start1, this.end1);
    }

    override get propertiesToSerialize() {
        return getAttributesToSerialize(this);
    }
}
